import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isWithinBounds,
  useGetFootprintingPigletHerdDetails,
  useMutateFootprintingPigletHerdDetails,
  useTranslation,
} from "utils";

import { Card, Flex, Form, FormInstance, InputNumber, PageSpin, Typography } from "components";

type PigletHerdDetailsProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
};

const PigletHerdDetailsForm = ({ primaryDataId, readOnly, farmId, year }: PigletHerdDetailsProps) => {
  const { t } = useTranslation("footprintingPiglet");
  const formRef = useRef<FormInstance>(null);

  const [averageNumberOfSows, setAverageNumberOfSows] = useState<number | null>(null);
  const [yearlyReplacementRatePct, setYearlyReplacementRatePct] = useState<number | null>(null);
  const [sucklingPigletLossRatePct, setSucklingPigletLossRatePct] = useState<number | null>(null);
  const [rearingLossPct, setRearingLossPct] = useState<number | null>(null);
  const [soldSucklingPigsPct, setSoldSucklingPigsPct] = useState<number | null>(null);

  const { data: herdDetails, isSuccess, isLoading } = useGetFootprintingPigletHerdDetails(primaryDataId ?? "", { enabled: !!primaryDataId });

  const { mutate: mutateHerdDetails } = useMutateFootprintingPigletHerdDetails(
    farmId,
    year,
    { averageNumberOfSows, yearlyReplacementRatePct, sucklingPigletLossRatePct, rearingLossPct, soldSucklingPigsPct },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateHerdDetails();
      }
    },
    [isLoading, mutateHerdDetails, readOnly]
  );

  useEffect(() => {
    if (herdDetails && isSuccess) {
      setAverageNumberOfSows(herdDetails.averageNumberOfSows ?? null);
      setYearlyReplacementRatePct(herdDetails.yearlyReplacementRatePct ?? null);
      setSucklingPigletLossRatePct(herdDetails.sucklingPigletLossRatePct ?? null);
      setRearingLossPct(herdDetails.rearingLossPct ?? null);
      setSoldSucklingPigsPct(herdDetails.soldSucklingPigsPct ?? null);

      formRef.current?.setFieldsValue({
        averageNumberOfSows: herdDetails.averageNumberOfSows ?? null,
        yearlyReplacementRatePct: formatFractionInput(herdDetails.yearlyReplacementRatePct ?? null),
        sucklingPigletLossRatePct: formatFractionInput(herdDetails.sucklingPigletLossRatePct ?? null),
        rearingLossPct: formatFractionInput(herdDetails.rearingLossPct ?? null),
        soldSucklingPigsPct: formatFractionInput(herdDetails.soldSucklingPigsPct ?? null),
      });

      const fieldsToValidate = (
        ["averageNumberOfSows", "yearlyReplacementRatePct", "sucklingPigletLossRatePct", "rearingLossPct", "soldSucklingPigsPct"] as const
      ).some((field) => herdDetails[field] !== null && herdDetails[field] !== undefined);

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [herdDetails, isSuccess]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div data-testid="footprinting-piglet-herd-details-form">
      <Form ref={formRef}>
        <Flex vertical gap={16}>
          <Card border className="padding-200">
            <Flex vertical gap={16}>
              <Flex vertical gap={8}>
                <Typography variant="h4">{t("pigletHerdDetailsForm.averageNumberOfSowsQuestion")}</Typography>
                <Typography variant="paragraph-m" color="grey-600">
                  {t("pigletHerdDetailsForm.averageNumberOfSowsQuestionDescription")}
                </Typography>
              </Flex>
              <InputNumber
                disabled={readOnly}
                name="averageNumberOfSows"
                dataTestId="footprinting-average-number-of-sows-input"
                label={t("pigletHerdDetailsForm.question.averageNumberOfSowsInput")}
                suffix={t("pigletHerdDetailsForm.question.averageNumberOfSowsSuffix")}
                value={averageNumberOfSows}
                initialValue={averageNumberOfSows}
                onChange={(v) => setAverageNumberOfSows(v)}
                validateFirst
                rules={[
                  { required: true, type: "number", message: t("pigletHerdDetailsForm.error.validNumberSows") },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("pigletHerdDetailsForm.error.validNumberSows")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Flex>
          </Card>

          <Card border className="padding-200">
            <Flex vertical gap={16}>
              <Flex vertical gap={8}>
                <Typography variant="h4">{t("pigletHerdDetailsForm.yearlyReplacementRateQuestion")}</Typography>
                <Typography variant="paragraph-m" color="grey-600">
                  {t("pigletHerdDetailsForm.yearlyReplacementRateQuestionDescription")}
                </Typography>
              </Flex>
              <InputNumber
                disabled={readOnly}
                name="yearlyReplacementRatePct"
                dataTestId="footprinting-yearly-replacement-rate-input"
                label={t("pigletHerdDetailsForm.question.yearlyReplacementRateInput")}
                suffix={t("pigletHerdDetailsForm.question.yearlyReplacementRateSuffix")}
                value={formatFractionInput(yearlyReplacementRatePct)}
                initialValue={formatFractionInput(yearlyReplacementRatePct)}
                onChange={(v) => setYearlyReplacementRatePct(formatPercentageInput(v))}
                validateFirst
                rules={[
                  { required: true, type: "number", message: t("pigletHerdDetailsForm.error.validPercentage") },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isWithinBounds(v, 0, 100)) {
                        return Promise.reject(new Error(t("pigletHerdDetailsForm.error.validPercentage")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Flex>
          </Card>

          <Card border className="padding-200">
            <Flex vertical gap={16}>
              <Flex vertical gap={8}>
                <Typography variant="h4">{t("pigletHerdDetailsForm.sucklingPigletLossRateQuestion")}</Typography>
                <Typography variant="paragraph-m" color="grey-600">
                  {t("pigletHerdDetailsForm.sucklingPigletLossRateQuestionDescription")}
                </Typography>
              </Flex>
              <InputNumber
                disabled={readOnly}
                name="sucklingPigletLossRatePct"
                dataTestId="footprinting-suckling-piglet-loss-rate-input"
                label={t("pigletHerdDetailsForm.question.sucklingPigletLossRateInput")}
                suffix={t("pigletHerdDetailsForm.question.sucklingPigletLossRateSuffix")}
                value={formatFractionInput(sucklingPigletLossRatePct)}
                initialValue={formatFractionInput(sucklingPigletLossRatePct)}
                onChange={(v) => setSucklingPigletLossRatePct(formatPercentageInput(v))}
                validateFirst
                rules={[
                  { required: true, type: "number", message: t("pigletHerdDetailsForm.error.validPercentage") },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isWithinBounds(v, 0, 100)) {
                        return Promise.reject(new Error(t("pigletHerdDetailsForm.error.validPercentage")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Flex>
          </Card>

          <Card border className="padding-200">
            <Flex vertical gap={16}>
              <Flex vertical gap={8}>
                <Typography variant="h4">{t("pigletHerdDetailsForm.rearingLossQuestion")}</Typography>
                <Typography variant="paragraph-m" color="grey-600">
                  {t("pigletHerdDetailsForm.rearingLossQuestionDescription")}
                </Typography>
              </Flex>
              <InputNumber
                disabled={readOnly}
                name="rearingLossPct"
                dataTestId="footprinting-rearing-loss-input"
                label={t("pigletHerdDetailsForm.question.rearingLossInput")}
                suffix={t("pigletHerdDetailsForm.question.rearingLossSuffix")}
                value={formatFractionInput(rearingLossPct)}
                initialValue={formatFractionInput(rearingLossPct)}
                onChange={(v) => setRearingLossPct(formatPercentageInput(v))}
                validateFirst
                rules={[
                  { required: true, type: "number", message: t("pigletHerdDetailsForm.error.validPercentage") },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isWithinBounds(v, 0, 100)) {
                        return Promise.reject(new Error(t("pigletHerdDetailsForm.error.validPercentage")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Flex>
          </Card>

          <Card border className="padding-200">
            <Flex vertical gap={16}>
              <Flex vertical gap={8}>
                <Typography variant="h4">{t("pigletHerdDetailsForm.soldSucklingPigsQuestion")}</Typography>
                <Typography variant="paragraph-m" color="grey-600">
                  {t("pigletHerdDetailsForm.soldSucklingPigsQuestionDescription")}
                </Typography>
              </Flex>
              <InputNumber
                disabled={readOnly}
                name="soldSucklingPigsPct"
                dataTestId="footprinting-sold-suckling-pigs-input"
                label={t("pigletHerdDetailsForm.question.soldSucklingPigsInput")}
                suffix={t("pigletHerdDetailsForm.question.soldSucklingPigsSuffix")}
                value={formatFractionInput(soldSucklingPigsPct)}
                initialValue={formatFractionInput(soldSucklingPigsPct)}
                onChange={(v) => setSoldSucklingPigsPct(formatPercentageInput(v))}
                validateFirst
                rules={[
                  { required: true, type: "number", message: t("pigletHerdDetailsForm.error.validPercentage") },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isWithinBounds(v, 0, 100)) {
                        return Promise.reject(new Error(t("pigletHerdDetailsForm.error.validPercentage")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Flex>
          </Card>
        </Flex>
      </Form>
    </div>
  );
};

PigletHerdDetailsForm.Key = "piglet.herdDetails";
export { PigletHerdDetailsForm };
